import { Alerts } from 'constants/alerts';
import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import { useApiContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { BankOperationStatusChipField } from 'pages/Finance/RequestsBankOps/components/BankOperationStatusChipField/BankOperationStatusChipField';
import { RequestsBankOpsFinanceUserAlert } from 'pages/Finance/RequestsBankOps/components/RequestsBankOpsFinanceUserAlert';
import { useRequestsBankOpsEditable } from 'pages/Finance/RequestsBankOps/hooks/useRequestsBankOpsEditable';
import { RecordContextProvider, useListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { Typography } from 'shared/mui/Typography';
import { DatagridBody } from 'shared/react-admin/DatagridBodies/DatagridBody/DatagridBody';
import {
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  TextLinkField,
  TooltipTextField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { IconButtonStyled } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField.styled';
import { BankOperation } from 'types/embedded/bankOperation';
import { Permission } from 'types/embedded/permission';
import { User } from 'types/embedded/user';
import { formatCurrency } from 'utils';

import { DatagridStyled } from './RequestsBankOpsListDatagrid.styled';
import { RequestsBankOpsListDatagridBulk } from './RequestsBankOpsListDatagridBulk/RequestsBankOpsListDatagridBulk';
import { RequestsBankOpsListDatagridHeader } from './RequestsBankOpsListDatagridHeader';
import { RequestsBankOpsListDatagridRow } from './RequestsBankOpsListDatagridRow';

type Props = {
  handleOpenDeleteModal: (record: BankOperation) => void;
  handleOpenMerchantAccountModal: (id?: string) => void;
  handleOpenRecordModalComment: (record: BankOperation) => void;
};

export const RequestsBankOpsListDatagrid: React.FC<Props> = ({
  handleOpenDeleteModal,
  handleOpenMerchantAccountModal,
  handleOpenRecordModalComment,
}) => {
  const { hasResourceAccess, hasResourcePermissions, isHaveRole } = usePermissions();

  const { isEdit, isDelete } = useRequestsBankOpsEditable();
  const { total, filterValues } = useListContext();

  const { user } = useApiContext();
  const showEmptyListAlert = Boolean(total === 0 && !Object.keys(filterValues).length);

  if (user.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
    return (
      <RequestsBankOpsFinanceUserAlert userLastName={user.lastName} userName={user.firstName} />
    );
  }

  if (showEmptyListAlert) {
    return (
      <Alert severity="info">
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Нет созданных "Заявок на банковские операции". Создайте заявку, нажав на кнопку “Создать”.
      </Alert>
    );
  }

  return (
    <DatagridStyled
      body={<DatagridBody row={<RequestsBankOpsListDatagridRow />} />}
      bulkActionsToolbar={<RequestsBankOpsListDatagridBulk />}
      header={<RequestsBankOpsListDatagridHeader />}
      resizableColumns
    >
      <TextLinkField
        cellClassName="resize-originId"
        headerClassName="resize-originId"
        label="ID"
        resource="bank_operations"
        sortBy="id"
        source="originId"
        type="show"
      />
      <DateTimeField
        cellClassName="resize-dateOfOperation"
        dateMask="dd.MM.yyyy"
        headerClassName="resize-dateOfOperation"
        label="Дата в ПС"
        source="dateOfOperation"
        timeMask={false}
        timezone={filterValues.timezone}
      />
      <EmbeddedFunctionField<BankOperation>
        cellClassName="resize-type"
        headerClassName="resize-type"
        label="Тип операции"
        sortable={false}
        sourceField="type.description"
        sx={{ textWrap: 'nowrap' }}
      />
      <EmbeddedFunctionField<BankOperation>
        cellClassName="resize-direction"
        headerClassName="resize-direction"
        label="Направление"
        sourceField="direction.description"
      />
      <FunctionField
        cellClassName="resize-amountPenny"
        headerClassName="resize-amountPenny"
        label="Сумма"
        render={({ amountPenny }: BankOperation) => {
          return formatCurrency(Number(amountPenny));
        }}
        sortable={false}
        source="amountPenny"
      />
      {hasResourceAccess(Resources.Currencies) && (
        <EmbeddedFunctionField<BankOperation>
          cellClassName="resize-currency"
          fontWeight={500}
          headerClassName="resize-currency"
          label="Валюта"
          sortable={false}
          sourceField="currency.alphaCode"
        />
      )}
      <FunctionField<BankOperation>
        cellClassName="resize-status"
        headerClassName="resize-status"
        label="Статус"
        render={(record) => {
          return (
            <RecordContextProvider value={record?.status}>
              <BankOperationStatusChipField source="description">
                {Boolean(record?.commentCount) && (
                  <IconButtonStyled onClick={() => handleOpenRecordModalComment(record)}>
                    <ModeCommentOutlinedIcon />
                  </IconButtonStyled>
                )}
              </BankOperationStatusChipField>
            </RecordContextProvider>
          );
        }}
      />
      {hasResourceAccess('referrals') && (
        <EmbeddedFunctionField<BankOperation>
          cellClassName="resize-referral"
          headerClassName="resize-referral"
          label="Реферал"
          sortable={false}
          sourceField="referral.name"
        />
      )}
      {hasResourceAccess('merchants') && (
        <EmbeddedFunctionField<BankOperation>
          cellClassName="resize-merchant"
          headerClassName="resize-merchant"
          label="Мерчант"
          sortable={false}
          sourceField="merchant.name"
        />
      )}
      {hasResourceAccess(Resources.Providers) && (
        <EmbeddedFunctionField<BankOperation>
          cellClassName="resize-provider"
          headerClassName="resize-provider"
          label="Провайдер"
          sortable={false}
          sourceField="provider.name"
        />
      )}
      <FunctionField
        cellClassName="resize-merchantAccount"
        headerClassName="resize-merchantAccount"
        label="Счет мерчанта"
        render={(record: BankOperation) => (
          <Typography
            color="secondary"
            onClick={() => handleOpenMerchantAccountModal(record.merchantAccount?.['@id'])}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              textWrap: 'nowrap',
            }}
            variant="body2"
          >
            {record.merchantAccount?.name}
          </Typography>
        )}
        sortable={false}
        source="merchantAccount.name"
      />
      <TooltipTextField
        cellClassName="resize-description"
        headerClassName="resize-description"
        label="Комментарий"
        source="description"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textDecoration: 'none',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      />
      {isHaveRole([Role.FinanceLead, Role.Admin]) && (
        <EmbeddedFunctionField<BankOperation>
          cellClassName="resize-user"
          headerClassName="resize-user"
          label="Автор"
          render={(value: User) => {
            return (
              <RecordContextProvider value={value}>
                <UserProfileField />
              </RecordContextProvider>
            );
          }}
          sortable={false}
          sourceField="user"
        />
      )}
      <DateTimeField
        cellClassName="resize-createdAt"
        headerClassName="resize-createdAt"
        label="Дата создания"
        sortable={false}
        source="createdAt"
        timezone={filterValues.timezone}
      />
      <DateTimeField
        cellClassName="resize-updatedAt"
        headerClassName="resize-updatedAt"
        label="Дата редактир."
        sortable={false}
        source="updatedAt"
        timezone={filterValues.timezone}
      />
      <ContextMenu
        render={(record: BankOperation) => [
          <ContextMenuShowButton
            key="show-button"
            linkProps={{ resource: Resources.RequestBankOperations }}
          />,
          hasResourcePermissions(Resources.RequestBankOperations, [
            Permission.Full,
            Permission.Update,
          ]) &&
            isEdit(record as BankOperation) && (
              <ContextMenuEditButton
                key="edit-button"
                linkProps={{ resource: Resources.RequestBankOperations }}
              />
            ),
          hasResourcePermissions(Resources.RequestBankOperations, [
            Permission.Full,
            Permission.Delete,
          ]) &&
            isDelete(record as BankOperation) && (
              <ContextMenuDeleteButton
                key="delete-button"
                onClick={() => handleOpenDeleteModal(record)}
              />
            ),
        ]}
      />
    </DatagridStyled>
  );
};
